import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard, FileText, Upload, Search, Database, Code,
  Settings, LogOut, Menu, X, Globe, Shield, ChevronRight, Sparkles
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin','staff'] },
  { to: '/records', icon: FileText, label: 'Records', roles: ['admin','staff'] },
  { to: '/upload', icon: Upload, label: 'Upload', roles: ['admin','staff'] },
  { to: '/search', icon: Search, label: 'Search', roles: ['admin','staff'] },
  { to: '/ask', icon: Sparkles, label: 'Ask the records', roles: ['admin','staff'] },
  { to: '/nl-search', icon: Code, label: 'Smart Query', roles: ['admin','staff'] },
  { to: '/migration', icon: Database, label: 'Migration', roles: ['admin','staff'] },
  { to: '/admin', icon: Shield, label: 'Admin', roles: ['admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const filtered = navItems.filter(n => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-100 flex flex-col transform transition-transform duration-200 ease-in-out
        ${open ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <img src="/cognivault_logo.png" alt="CogniVault" className="h-10 w-auto object-contain" />
          <button className="ml-auto md:hidden" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {filtered.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                ${isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
              <Icon size={16} />
              {label}
              {({ isActive }) => isActive && <ChevronRight size={14} className="ml-auto opacity-50" />}
            </NavLink>
          ))}
          
          <div className="pt-3 mt-3 border-t border-gray-100">
            <a href="/public" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all">
              <Globe size={16} />
              Public Portal
            </a>
          </div>
        </nav>

        {/* User */}
        <div className="p-3 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
              <div className="text-xs text-gray-400 capitalize">{user?.role}</div>
            </div>
            <button onClick={handleLogout} className="text-gray-400 hover:text-gray-600 transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {open && <div className="fixed inset-0 z-40 bg-black/20 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100">
          <button onClick={() => setOpen(true)}><Menu size={20} /></button>
          <img src="/cognivault_logo.png" alt="CogniVault" className="h-12 w-auto object-contain" />
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
